import { Carousel, LazyImage, Text, TwistIcons } from '@doist/marketist'

import { Document } from '@contentful/rich-text-types'
import { Interpolate } from 'components'
import { OrderedList } from 'components/RichText'
import { useTranslation } from 'next-i18next'
import { parseRichText } from 'services/contentful'
import { Integration } from 'types/contentful'

import { SCROLL_ANCHOR } from '../'

import css from './IntegrationAppDetails.module.css'

type IntegrationAppDetailsProps = {
    integration: Integration
}

function FeatureItem({ text, isZapier }: { text: string; isZapier: boolean }) {
    const Icon = isZapier ? TwistIcons.LightningBolt : TwistIcons.Star

    return (
        <Text tag="li" size="body-base">
            <Icon className={css.iconContainer} aria-hidden />
            {text}
        </Text>
    )
}

export function IntegrationAppDetails({ integration }: IntegrationAppDetailsProps) {
    const { t } = useTranslation(['integrations', 'common'])
    const { features, commonContent } = integration
    const screenshots = commonContent?.screenshotsCollection?.items
    const aboutRichContent = commonContent?.about?.json as Document | undefined

    return (
        <main className={css.main}>
            {aboutRichContent && (
                <section className={css.about}>{parseRichText(aboutRichContent)}</section>
            )}

            {screenshots && screenshots?.length > 0 && (
                <section className={css.carousel}>
                    <Carousel
                        nextButtonLabel={t('next', { ns: 'common' })}
                        previousButtonLabel={t('previous', { ns: 'common' })}
                    >
                        {screenshots.map(({ title, url }) => (
                            <LazyImage key={url} alt={title} src={url} />
                        ))}
                    </Carousel>
                </section>
            )}

            {features && (
                <section className={css.features}>
                    <Text tag="h3" size="major-base" className={css.featureTitle}>
                        {integration.zapier ? t('zapier.popularConnections') : t('features')}
                    </Text>

                    <ul>
                        {features.map((text, i) => (
                            <FeatureItem
                                key={`feature-item-${i}`}
                                text={text}
                                isZapier={integration.zapier}
                            />
                        ))}
                    </ul>
                </section>
            )}

            {integration.zapier && (
                <section className={css.howToConnect}>
                    <div className={css.scrollAnchor} id={SCROLL_ANCHOR} />

                    <Text tag="h3" size="major-sm">
                        {t('zapier.connect.title')}
                    </Text>

                    <OrderedList className={css.orderedList}>
                        {[...Array(6)].map((_, i) => (
                            <Text className={css.connectLi} tag="li" size="body-base" key={i}>
                                <Interpolate t={t} i18nKey={`zapier.connect.${i + 1}`} />
                            </Text>
                        ))}
                    </OrderedList>
                </section>
            )}
        </main>
    )
}
